import { Head, Link, router } from "@inertiajs/react"
import { Pencil } from "lucide-react"

import { FileIcon } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import {
  companyDocumentPath,
  companyDocumentsPath,
  companyResourcesPath,
  editCompanyDocumentPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface Document {
  id: number
  name: string
  document_date: string | null
  notes: string | null
  filename: string | null
  file_type: string | null
  file_url: string | null
  last_changed: string | null
  last_changed_by: string | null
  changelog: string | null
}

interface Props {
  document: Document
  can_edit: boolean
  can_destroy: boolean
}

/* ── Helpers ────────────────────────────────────────────── */

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  })
}

/* ── Main Component ────────────────────────────────────── */

export default function DocumentShow({
  document: doc,
  can_edit,
  can_destroy,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Company", href: companyResourcesPath() },
    { title: "Documents", href: companyDocumentsPath() },
    { title: doc.name, href: companyDocumentPath(doc.id) },
  ]

  const handleDelete = () => {
    if (confirm("Are you sure you want to delete this document?")) {
      router.delete(companyDocumentPath(doc.id))
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Document - ${doc.name}`} />

      <div className="p-6">
        <div className="mb-6 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">View Document</h1>
          <div className="flex items-center gap-2">
            {can_edit && (
              <Link
                href={editCompanyDocumentPath(doc.id)}
                className="border-hpi-light-gray hover:bg-hpi-pale-blue flex items-center gap-1.5 rounded border px-4 py-2 text-sm font-medium"
              >
                <Pencil className="h-4 w-4" />
                Edit
              </Link>
            )}
            {can_destroy && (
              <button
                type="button"
                onClick={handleDelete}
                className="bg-hpi-red rounded px-4 py-2 text-sm text-white hover:bg-red-700"
              >
                Delete
              </button>
            )}
          </div>
        </div>

        <div className="border-hpi-light-gray max-w-2xl rounded-lg border bg-white p-6">
          <dl className="space-y-4">
            <div>
              <dt className="text-hpi-med-gray text-sm font-medium">Date</dt>
              <dd className="text-hpi-dark-gray mt-1">
                {formatDate(doc.document_date)}
              </dd>
            </div>

            {doc.name && (
              <div>
                <dt className="text-hpi-med-gray text-sm font-medium">
                  Brief Description
                </dt>
                <dd className="text-hpi-dark-gray mt-1">{doc.name}</dd>
              </div>
            )}

            {doc.notes && (
              <div>
                <dt className="text-hpi-med-gray text-sm font-medium">Notes</dt>
                <dd className="text-hpi-dark-gray mt-1 whitespace-pre-wrap">
                  {doc.notes}
                </dd>
              </div>
            )}

            {doc.filename && (
              <div>
                <dt className="text-hpi-med-gray text-sm font-medium">
                  View Document
                </dt>
                <dd className="mt-1">
                  {doc.file_url ? (
                    <a
                      href={doc.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-hpi-active-blue inline-flex items-center gap-2 text-lg hover:underline"
                    >
                      <FileIcon fileType={doc.file_type} className="h-8 w-8" />
                      <span className="text-sm">{doc.filename}</span>
                    </a>
                  ) : (
                    <div className="text-hpi-dark-gray flex items-center gap-2">
                      <FileIcon fileType={doc.file_type} className="h-8 w-8" />
                      <span className="text-sm">{doc.filename}</span>
                    </div>
                  )}
                </dd>
              </div>
            )}

            {doc.last_changed && (
              <div className="text-hpi-med-gray border-hpi-light-gray border-t pt-4 text-sm">
                Last change {formatDate(doc.last_changed)}
                {doc.last_changed_by ? ` by ${doc.last_changed_by}` : ""}
              </div>
            )}
          </dl>
        </div>

        {can_edit && doc.changelog && (
          <div className="border-hpi-light-gray bg-hpi-pale-blue mt-6 max-w-2xl rounded-lg border p-4">
            <h4 className="text-hpi-dark-gray mb-2 text-sm font-semibold">
              Change Log
            </h4>
            <div className="text-hpi-med-gray text-sm whitespace-pre-wrap">
              {doc.changelog}
            </div>
          </div>
        )}

        <div className="mt-6">
          <Link
            href={companyDocumentsPath()}
            className="text-hpi-active-blue text-sm hover:underline"
          >
            Return to Document List
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
